import type { CartPanelIMUpsellBlock } from '@/@types/content';
import Button from '../ui/Buttons/Button';
import Link from '../ui/Link';
import { formatPrice } from '@/utils/helpers';
import { usePostCartItemMutation } from '@/redux/api/client/cart';
import RichText from '../cms/RichText';
import classnames from 'classnames';
import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useNavigate } from 'react-router';
import { closeCartPanel } from '@/redux/slices/cartSlice';
import { AnalyticsContext } from '@/analytics/constants';
import {
  trackAddToCart,
  trackSelectPromotionEvent,
  trackViewPromotionEvent
} from '@/analytics/ecommerce';
import { useFetchAnalyticsItems } from '@/analytics/useFetchAnalyticsItems';
import { createTransactionId } from '@/analytics/analyticsUtils';
import { ItemListAnalyticsModel } from '@/@types/client-api';

export interface CartPanelIMUpsellBlockProps extends CartPanelIMUpsellBlock {
  close: () => void;
}

const renderTextWithNonBreakingTail = (text?: string) => {
  if (!text) return null;

  const words = text.trim().split(/\s+/);
  if (words.length < 3) return text;

  return (
    <>
      {words.slice(0, -2).join(' ')}{' '}
      <span className="whitespace-nowrap">{words.slice(-2).join(' ')}</span>
    </>
  );
};

const CartPanelIMUpsellBlockBody: React.FC<CartPanelIMUpsellBlockProps> = props => {
  const { getAnalyticItem } = useFetchAnalyticsItems();
  const analyticsEventTriggered = useRef(false);

  const handleTrackIMUpsellBlockPromotion = useCallback(async () => {
    if (analyticsEventTriggered.current) {
      return;
    }

    const promo = {
      creative_name: props?.image?.name?.toString() ?? '',
      creative_slot: props?.contentType?.[1] ?? '',
      promotion_id: props?.contentLink?.id?.toString() ?? '',
      promotion_name: props?.name ?? '',
      context: AnalyticsContext.CartPanel
    };

    const IMVariant = props?.catalogVariant?.at(0) as { code: string };
    const itemAnalyticsModel = await getAnalyticItem(IMVariant.code);
    trackViewPromotionEvent(promo, itemAnalyticsModel);
    analyticsEventTriggered.current = true;
  }, [props, getAnalyticItem]);

  useEffect(() => {
    handleTrackIMUpsellBlockPromotion();
  }, [handleTrackIMUpsellBlockPromotion]);

  const titleLine2Color = useMemo(() => {
    return classnames({
      'text-white': props.titleLine2Color === 'white',
      'text-black': props.titleLine2Color === 'black',
      'text-red': props.titleLine2Color === 'red',
      'text-gold': props.titleLine2Color === 'gold',
      'text-green-100': props.titleLine2Color === 'green-100',
      'text-green-200': props.titleLine2Color === 'green-200',
      'text-green-300': props.titleLine2Color === 'green-300',
      'text-teal-100': props.titleLine2Color === 'teal-100',
      'text-teal-200': props.titleLine2Color === 'teal-200',
      'text-teal-300': props.titleLine2Color === 'teal-300',
      'text-blue-100': props.titleLine2Color === 'blue-100',
      'text-blue-200': props.titleLine2Color === 'blue-200',
      'text-blue-300': props.titleLine2Color === 'blue-300',
      'text-indigo-100': props.titleLine2Color === 'indigo-100',
      'text-indigo-200': props.titleLine2Color === 'indigo-200',
      'text-indigo-300': props.titleLine2Color === 'indigo-300',
      'text-purple-100': props.titleLine2Color === 'purple-100',
      'text-purple-200': props.titleLine2Color === 'purple-200',
      'text-purple-300': props.titleLine2Color === 'purple-300',
      'text-magenta-100': props.titleLine2Color === 'magenta-100',
      'text-magenta-200': props.titleLine2Color === 'magenta-200',
      'text-magenta-300': props.titleLine2Color === 'magenta-300'
    });
  }, [props.titleLine2Color]);

  const variant = props.catalogVariant?.at(0) as { discountedPrice: number };
  const variantPrice = variant?.discountedPrice;
  const footerText = props.footerText?.replace('{price}', formatPrice(variantPrice) || '');
  const titleLine2Styles = classnames(titleLine2Color);

  const containerStyles = classnames('mx-6 sm:mx-16');
  const titlesStyles = classnames('heading-4 mb-10 tracking-normal sm:mb-14');
  const imgStyles = classnames('w-full rounded object-cover sm:w-[302px]');

  return (
    <div className={containerStyles}>
      <div className={titlesStyles}>
        <p>{renderTextWithNonBreakingTail(props.titleLine1)}</p>
        <p className={titleLine2Styles}>{renderTextWithNonBreakingTail(props.titleLine2)}</p>
      </div>
      <div className="mb-10">
        <img src={props?.image?.url} className={imgStyles} />
      </div>
      <div className={'mb-10'}>
        <RichText
          propertyName="Copy"
          checkList={true}
          checklistRed={true}
          className="rtf-im-upsell-copy"
          content={props.copy}
        />
      </div>
      <div>
        <p className="text-1-fixed text-gray-dark">{footerText}</p>
      </div>
    </div>
  );
};

const CartPanelIMUpsellBlockFooter: React.FC<CartPanelIMUpsellBlockProps> = props => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAnalyticItem } = useFetchAnalyticsItems();
  const [addCartItem, { isLoading, isSuccess }] = usePostCartItemMutation();
  const siteLabels = useSelector((state: RootState) => state.page.siteLabels);
  const itemListAnalyticsModel = useMemo(() => {
    return {
      listId: props.contentLink?.id?.toString(),
      listName: props.name
    } as ItemListAnalyticsModel;
  }, [props.contentLink?.id, props.name]);

  const variant = props.catalogVariant?.at(0) as { discountedPrice: number; code: string };
  const variantPrice = variant?.discountedPrice;
  const variantCode = variant?.code;
  const addToCartButtonLabel = `Add to cart • ${formatPrice(variantPrice)} / yr`;

  const cartPath = siteLabels?.cartUrl
    ? new URL(siteLabels.cartUrl, window.location.origin).pathname
    : undefined;

  const handleTrackEvents = useCallback(async () => {
    const IMVariant = props?.catalogVariant?.at(0) as { code: string };
    const itemAnalyticsModel = await getAnalyticItem(IMVariant.code);

    const promo = {
      creative_name: props?.image?.name?.toString() ?? '',
      creative_slot: props?.contentType?.[1] ?? '',
      promotion_id: props?.contentLink?.id?.toString() ?? '',
      promotion_name: props?.name ?? '',
      context: AnalyticsContext.CartPanel
    };
    trackSelectPromotionEvent(promo, itemAnalyticsModel);

    const transactionId = createTransactionId('');
    trackAddToCart(
      itemAnalyticsModel,
      AnalyticsContext.CartPanel,
      transactionId,
      itemListAnalyticsModel
    );
  }, [props, getAnalyticItem, itemListAnalyticsModel]);

  const handleAddToCart = () => {
    const { sourceCode, keyCode } = props;
    const priorityCode = sourceCode && keyCode ? `${sourceCode} ${keyCode}` : undefined;
    addCartItem({
      code: variantCode,
      quantity: 1,
      itemListAnalyticsModel: itemListAnalyticsModel,
      priorityCode
    });
    handleTrackEvents();
  };

  useEffect(() => {
    if (isSuccess && cartPath) {
      dispatch(closeCartPanel());
      navigate(cartPath);
    }
  }, [isSuccess, cartPath, dispatch, navigate]);

  return (
    <>
      <div className="py-4">
        <Button
          label={addToCartButtonLabel}
          loading={isLoading}
          size="large"
          className="w-full"
          color="red"
          onClick={handleAddToCart}
        />
      </div>
      <div className="flex justify-center">
        <Link
          href={'#'}
          text="No, thanks"
          variant="text-link-2"
          className="flex self-end"
          disabled={isLoading}
          onClick={() => props.close()}
        />
      </div>
    </>
  );
};

export { CartPanelIMUpsellBlockBody, CartPanelIMUpsellBlockFooter };
